import { type UseQueryOptions, useQuery } from '@tanstack/react-query';
import {
  type GetPortfolioBrandsV1WorkspacesWorkspaceIdBrandsGetData,
  type GetPortfolioBrandsV1WorkspacesWorkspaceIdBrandsGetError,
  type GetPortfolioBrandsV1WorkspacesWorkspaceIdBrandsGetResponse,
  getPortfolioBrandsV1WorkspacesWorkspaceIdBrandsGet,
} from '../../generated';

export type GetPortfolioBrandsQueryVariables = GetPortfolioBrandsV1WorkspacesWorkspaceIdBrandsGetData['path'] &
  GetPortfolioBrandsV1WorkspacesWorkspaceIdBrandsGetData['query'];

export type GetPortfolioBrandsQuery = {
  data: GetPortfolioBrandsV1WorkspacesWorkspaceIdBrandsGetResponse;
  total_count: number;
};

/**
 * Extracts the total_count from the Vizit-Total-Count response header
 */
function extractTotalCount(response: Response): number {
  const totalCountHeader = response.headers.get('vizit-total-count');
  return totalCountHeader ? Number.parseInt(totalCountHeader, 10) : 0;
}

export const useGetPortfolioBrandsQuery = <
  TData = GetPortfolioBrandsQuery,
  TError = GetPortfolioBrandsV1WorkspacesWorkspaceIdBrandsGetError,
>(
  { workspace_id, limit, offset, search }: GetPortfolioBrandsQueryVariables,
  options?: Omit<UseQueryOptions<GetPortfolioBrandsQuery, TError, TData>, 'queryKey' | 'queryFn'>,
) => {
  return useQuery<GetPortfolioBrandsQuery, TError, TData>({
    queryKey: ['GetPortfolioBrands', { workspace_id, limit, offset, search }],
    queryFn: async () => {
      const { data, response } = await getPortfolioBrandsV1WorkspacesWorkspaceIdBrandsGet({
        path: { workspace_id },
        query: { limit, offset, search },
        throwOnError: true,
      });
      return {
        data: data ?? [],
        total_count: extractTotalCount(response),
      };
    },
    ...options,
  });
};

useGetPortfolioBrandsQuery.getKey = ({ workspace_id, limit, offset, search }: GetPortfolioBrandsQueryVariables) => [
  'GetPortfolioBrands',
  { workspace_id, limit, offset, search },
];

useGetPortfolioBrandsQuery.fetcher = (
  { workspace_id, limit, offset, search }: GetPortfolioBrandsQueryVariables,
  _headers?: RequestInit['headers'],
) => {
  // Returns a function for backward compatibility with the old fetcher pattern
  return async () => {
    const { data, response } = await getPortfolioBrandsV1WorkspacesWorkspaceIdBrandsGet({
      path: { workspace_id },
      query: { limit, offset, search },
      throwOnError: true,
    });
    return {
      data: data ?? [],
      total_count: extractTotalCount(response),
    };
  };
};
